'use client';
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/enforces-negative-arbitrary-values */
import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { FeCarouselV2 } from '../FeCarousel';
import { MaterialIcon } from '../MaterialIcon';
import ProductGridCard, {
  ProductGridCardProps,
  RewardToggleColour,
  RewardToggleLabels,
} from '../ProductGridCard';
import { TRANSLATIONS } from './i18n';
import { resolveRewardToggleViewAllLabel } from './rewardToggleLabel';

type ProductCardGridProps = {
  items: ProductGridCardProps[];
  layoutType?: 'GRID' | 'CAROUSEL';
  rewardToggleColour?: RewardToggleColour;
  // Editor override for the collapsed toggle label, `{n}` = item count; blank → locale default.
  rewardToggleLabel?: string;
};

const ProductCardGridNavigationButton = ({
  icon,
}: {
  icon: 'arrow_left_alt' | 'arrow_right_alt';
}) => (
  <div className="flex size-[30px] items-center justify-center rounded-[15px] bg-fill/card shadow-md">
    <MaterialIcon icon={icon} px={20} className="text-current" />
  </div>
);

type ProductCardGridCardProps = {
  item: ProductGridCardProps;
  cardId: CardId;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  // GRID: lift the whole card above its siblings (multi-column, so there are cards to overlap).
  // CAROUSEL: false — a single horizontal row has nothing to overlap and swiper clips absolute
  // content, so the card just grows in place.
  canLift: boolean;
};

const ProductCardGridCard = ({
  item,
  cardId,
  expanded,
  onExpandedChange,
  canLift,
}: ProductCardGridCardProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [reservedHeight, setReservedHeight] = useState<number>();

  const handleRewardExpandedChange = (next: boolean) => {
    if (canLift && next && wrapperRef.current) {
      // Capture the current (collapsed) cell height before the card floats out of flow, so the grid
      // cell keeps its size and siblings never move. The grid is multi-column at every breakpoint
      // (2 cols on mobile, 3 on tablet, 4 on desktop), so the lift applies on all viewports.
      setReservedHeight(wrapperRef.current.getBoundingClientRect().height);
    }
    onExpandedChange(next);
  };

  const isLifted = canLift && expanded;

  return (
    <div
      ref={wrapperRef}
      data-reward-card-id={cardId}
      className={classNames('relative size-full min-w-0', isLifted && 'z-30')}
      style={
        isLifted && reservedHeight ? { minHeight: reservedHeight } : undefined
      }
    >
      <ProductGridCard
        {...item}
        rewardExpanded={expanded}
        onRewardExpandedChange={handleRewardExpandedChange}
        className={classNames(
          '!w-full sm:!w-full xl:!w-full',
          isLifted ? 'absolute inset-x-0 top-0 !h-auto shadow-lg' : '!h-full',
        )}
      />
    </div>
  );
};

const normalizeCardItems = ({
  items,
  defaultApplyCtaLabel,
  defaultViewDetailsCtaLabel,
  rewardToggleColour,
  rewardToggleLabels,
}: {
  items: ProductGridCardProps[];
  defaultApplyCtaLabel: string;
  defaultViewDetailsCtaLabel: string;
  rewardToggleColour?: RewardToggleColour;
  rewardToggleLabels: RewardToggleLabels;
}) =>
  items.map((item) => ({
    ...item,
    primaryCtaLabel: item.primaryCtaLabel ?? defaultApplyCtaLabel,
    secondaryCtaLabel:
      item.secondaryCtaUrl || item.onSecondaryCtaClick
        ? (item.secondaryCtaLabel ?? defaultViewDetailsCtaLabel)
        : item.secondaryCtaLabel,
    rewardToggleColour,
    rewardToggleLabels,
  }));

type CardId = string | number;

type CardExpansionProps = {
  expandedId: CardId | null;
  onCardExpandedChange: (id: CardId, expanded: boolean) => void;
};

const cardIdFor = (item: ProductGridCardProps, index: number): CardId =>
  item.id ?? `product-card-grid-item-${index}`;

const ProductCardGridList = ({
  items,
  expandedId,
  onCardExpandedChange,
}: { items: ProductGridCardProps[] } & CardExpansionProps) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-spacing/gap/y-sm md:grid-cols-3 lg:grid-cols-4 lg:gap-x-4">
    {items.map((item, index) => {
      const id = cardIdFor(item, index);
      return (
        <ProductCardGridCard
          key={id}
          item={item}
          cardId={id}
          expanded={expandedId === id}
          onExpandedChange={(next) => onCardExpandedChange(id, next)}
          canLift
        />
      );
    })}
  </div>
);

const ProductCardGridCarousel = ({
  items,
  showNavigation,
  shouldAutoplay,
  prevButtonClassName,
  nextButtonClassName,
  expandedId,
  onCardExpandedChange,
}: {
  items: ProductGridCardProps[];
  showNavigation: boolean;
  shouldAutoplay: boolean;
  prevButtonClassName: string;
  nextButtonClassName: string;
} & CardExpansionProps) => (
  <div className="relative ">
    <FeCarouselV2
      autoplay={
        shouldAutoplay
          ? {
              delay: 2500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false
      }
      navigation={
        showNavigation
          ? {
              showOnDesktop: true,
              showOnMobile: true,
              prevElClassName: `!left-[-22px] lg:!left-[-34px] ${prevButtonClassName}`,
              nextElClassName: `!right-[-22px] lg:!right-[-34px] ${nextButtonClassName}`,
              disabledClassName: '!text-text/disabled',
              prevElement: (
                <ProductCardGridNavigationButton icon="arrow_left_alt" />
              ),
              nextElement: (
                <ProductCardGridNavigationButton icon="arrow_right_alt" />
              ),
            }
          : undefined
      }
      beforeInitSlideClassName="mr-3 w-[calc((100%-24px)/2)] md:w-[calc((100%-36px)/3)] lg:mr-4 lg:w-[calc((100%-64px)/4)]"
      slideClassName="!flex !h-auto"
      slidesPerView={2}
      slidesPerViewBreakpoints={[
        [3, 768],
        [4, 960],
      ]}
      spaceBetween={12}
      spaceBetweenBreakpoints={[[16, 960]]}
    >
      {items.map((item, index) => {
        const id = cardIdFor(item, index);
        return (
          <ProductCardGridCard
            key={id}
            item={item}
            cardId={id}
            expanded={expandedId === id}
            onExpandedChange={(next) => onCardExpandedChange(id, next)}
            canLift={false}
          />
        );
      })}
    </FeCarouselV2>
  </div>
);

const ProductCardGrid: React.FC<ProductCardGridProps> = ({
  items,
  layoutType = 'GRID',
  rewardToggleColour,
  rewardToggleLabel,
}) => {
  const navigationId = useId().replace(/:/g, '');
  const context = useCmsClientContext();
  const translations = getClientTranslations(TRANSLATIONS, context);
  const rewardToggleLabels: RewardToggleLabels = {
    viewAll: (count: number) =>
      resolveRewardToggleViewAllLabel(rewardToggleLabel, count) ??
      translations.product_card_grid_view_all_rewards.replace(
        '{count}',
        String(count),
      ),
    viewLess: translations.product_card_grid_view_less,
    worth: translations.product_card_grid_worth,
  };
  const cardItems = normalizeCardItems({
    items,
    defaultApplyCtaLabel: translations.product_card_grid_apply_now,
    defaultViewDetailsCtaLabel: translations.product_card_grid_view_details,
    rewardToggleColour,
    rewardToggleLabels,
  });
  const isCarouselLayout = layoutType === 'CAROUSEL';
  const showCarouselNavigation = isCarouselLayout && cardItems.length > 2;
  // The Reward Toggle expands inline; auto-rotation would scroll the open card away and fights
  // swiper's slide-height equalisation. Keep autoplay only when no card can render a toggle
  // (i.e. none has reward preview items); otherwise the carousel is manual-navigation only.
  const anyCardHasRewards = cardItems.some(
    (card) => (card.previewItems?.length ?? 0) > 0,
  );
  const shouldAutoplayCarousel =
    isCarouselLayout && cardItems.length > 2 && !anyCardHasRewards;

  // Only one reward toggle open at a time across the grid.
  const [expandedId, setExpandedId] = useState<CardId | null>(null);
  const onCardExpandedChange = (id: CardId, expanded: boolean) =>
    setExpandedId(expanded ? id : null);

  // Close the open card on click/tap outside it or on Escape.
  useEffect(() => {
    if (expandedId == null) {
      return;
    }
    const openCard = document.querySelector(
      `[data-reward-card-id="${CSS.escape(String(expandedId))}"]`,
    );
    // pointerdown (not mousedown) so an outside tap closes the card on touch devices too, not only
    // with a mouse — the requirement is "click/tap outside to close".
    const onPointerDown = (event: PointerEvent) => {
      if (openCard && !openCard.contains(event.target as Node)) {
        setExpandedId(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpandedId(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [expandedId]);

  if (!cardItems.length) {
    return null;
  }

  return (
    <section
      className={classNames(
        'w-[calc(100%+var(--spacing-padding-section-x)*2)] px-spacing/padding/section-x -mx-spacing/padding/section-x',
        // overflow-hidden is only needed for the carousel full-bleed trick; in GRID it would clip a
        // lifted card that grows past the grid's bottom edge (e.g. an expanded card in the last row).
        isCarouselLayout && 'overflow-hidden',
      )}
      data-component="product-card-grid"
      data-layout-type={layoutType.toLowerCase()}
    >
      {isCarouselLayout ? (
        <ProductCardGridCarousel
          items={cardItems}
          showNavigation={showCarouselNavigation}
          shouldAutoplay={shouldAutoplayCarousel}
          prevButtonClassName={`product-card-grid-prev-${navigationId}`}
          nextButtonClassName={`product-card-grid-next-${navigationId}`}
          expandedId={expandedId}
          onCardExpandedChange={onCardExpandedChange}
        />
      ) : (
        <ProductCardGridList
          items={cardItems}
          expandedId={expandedId}
          onCardExpandedChange={onCardExpandedChange}
        />
      )}
    </section>
  );
};

export default ProductCardGrid;
